import React, { useState, useEffect, useCallback } from 'react';
import { Layout, PageHeader } from '../components/Layout';
import { useAuth, twoFactorApi, loginLogsApi, usersApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { BillingSettingsTab } from '../components/settings/BillingSettingsTab';
import { LoginLogsTab } from '../components/settings/LoginLogsTab';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  User, Shield, Bell, Users, Save, Mail, Send, CheckCircle, XCircle, 
  AlertCircle, Plus, Pencil, Trash2, FileText, Building, Clock, Loader2,
  ShieldCheck, ShieldOff, QrCode, Key, Download, History, RefreshCw
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const roleConfig = {
  admin: { label: 'Administrateur', color: 'bg-red-100 text-red-800' },
  gestionnaire: { label: 'Gestionnaire', color: 'bg-blue-100 text-blue-800' },
  utilisateur: { label: 'Utilisateur', color: 'bg-slate-100 text-slate-700' },
};

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-800', icon: Clock },
  active: { label: 'Actif', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  suspended: { label: 'Suspendu', color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
  blocked: { label: 'Bloqué', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const emailTemplateLabels = {
  account_pending: "Compte en attente de validation",
  account_approved: "Compte approuvé",
  account_rejected: "Compte rejeté",
  new_registration_admin: "Nouvelle inscription (admin)",
  quote_sent: "Devis envoyé",
  quote_accepted: "Devis accepté",
  quote_refused: "Devis refusé",
  quote_accepted_notify_manager: "quote_accepted_notify_manager",
  invoice_sent: "Facture envoyée",
  payment_reminder: "Rappel de paiement",
  payment_reminder_j7: "payment_reminder_j7",
  payment_reminder_j14: "payment_reminder_j14",
  payment_reminder_j21: "payment_reminder_j21",
  reservation_confirmed: "Réservation confirmée",
  reservation_cancelled: "Réservation annulée"
};

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  
  // Profile
  const [profileData, setProfileData] = useState({ first_name: '', last_name: '', email: '' });
  
  // Users
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    email: '', password: '', first_name: '', last_name: '', role: 'utilisateur', status: 'active'
  });
  
  // SMTP
  const [smtpConfig, setSmtpConfig] = useState({
    email: '', password: '', smtp_server: '', smtp_port: 587, use_tls: true, use_ssl: false
  });
  const [smtpStatus, setSmtpStatus] = useState({ is_connected: false, last_test: null });
  const [testingSmtp, setTestingSmtp] = useState(false);
  
  // Email Templates
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  
  // Billing Settings
  const [billingSettings, setBillingSettings] = useState({
    company_name: 'Diocèse de Pamiers',
    company_address: '',
    company_phone: '',
    company_email: '',
    company_siret: '',
    quote_validity_days: 30,
    payment_terms_days: 30,
    quote_header_text: '',
    quote_footer_text: '',
    invoice_header_text: '',
    invoice_footer_text: '',
    bank_beneficiary_name: '',
    iban: '',
    bic: '',
    payment_conditions: '',
    logo_url: '',
    auto_reminders_enabled: true,
    reminder_j7_enabled: true,
    reminder_j14_enabled: true,
    reminder_j21_enabled: true
  });

  // Reminder Scheduler State
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [pendingReminders, setPendingReminders] = useState(null);
  const [loadingScheduler, setLoadingScheduler] = useState(false);
  const [processingReminders, setProcessingReminders] = useState(false);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // 2FA State
  const [twoFactorStatus, setTwoFactorStatus] = useState({ enabled: false, forced: false });
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [setting2FA, setSetting2FA] = useState(false);

  // Login Logs State
  const [loginLogs, setLoginLogs] = useState([]);
  const [loginLogsTotal, setLoginLogsTotal] = useState(0);
  const [loginLogsPage, setLoginLogsPage] = useState(1);
  const [loginLogsFilters, setLoginLogsFilters] = useState({
    start_date: '', end_date: '', email: '', status: ''
  });
  const [loginStats, setLoginStats] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileData({ first_name: user.first_name || '', last_name: user.last_name || '', email: user.email || '' });
    }
  }, [user]);

  useEffect(() => {
    if (user?.role === 'admin') {
      if (activeTab === 'users') loadUsers();
      if (activeTab === 'smtp') loadSmtpConfig();
      if (activeTab === 'emails') loadEmailTemplates();
      if (activeTab === 'billing') loadBillingSettings();
      if (activeTab === 'logs') { loadLoginLogs(); loadLoginStats(); }
    } else if (user?.role === 'gestionnaire' && activeTab === 'users') {
      loadPendingUsers();
    }
    if (activeTab === 'security') load2FAStatus();
  }, [activeTab, user]);  // eslint-disable-line react-hooks/exhaustive-deps

  // 2FA Handlers
  const load2FAStatus = async () => {
    try {
      const res = await twoFactorApi.status();
      setTwoFactorStatus(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du statut 2FA');
    }
  };

  const handleSetup2FA = async () => {
    setSetting2FA(true);
    try {
      const res = await twoFactorApi.setup();
      setSetupData(res.data);
      setSetupDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la configuration 2FA');
    } finally {
      setSetting2FA(false);
    }
  };

  const handleVerifySetup = async (e) => {
    e.preventDefault();
    setSetting2FA(true);
    try {
      await twoFactorApi.verifySetup(verificationCode);
      toast.success('Authentification 2FA activée avec succès !');
      setSetupDialogOpen(false);
      setSetupData(null);
      setVerificationCode('');
      load2FAStatus();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code de vérification invalide');
    } finally {
      setSetting2FA(false);
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    setSetting2FA(true);
    try {
      await twoFactorApi.disable(verificationCode);
      toast.success('Authentification 2FA désactivée');
      setDisableDialogOpen(false);
      setVerificationCode('');
      load2FAStatus();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la désactivation');
    } finally {
      setSetting2FA(false);
    }
  };

  const handleForce2FA = async (userId, forceEnabled) => {
    try {
      await usersApi.force2FA(userId, forceEnabled);
      toast.success(forceEnabled ? '2FA forcée pour cet utilisateur' : '2FA désactivée de force');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleReset2FA = async (userId) => {
    try {
      await usersApi.reset2FA(userId);
      toast.success('2FA réinitialisée pour cet utilisateur');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Login Logs Handlers
  const loadLoginLogs = async () => {
    setLoadingLogs(true);
    try {
      const params = {
        page: loginLogsPage,
        page_size: 20,
        ...loginLogsFilters
      };
      // Remove empty values
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const res = await loginLogsApi.list(params);
      setLoginLogs(res.data.logs);
      setLoginLogsTotal(res.data.total);
    } catch (error) {
      toast.error('Erreur lors du chargement des logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const loadLoginStats = async () => {
    try {
      const res = await loginLogsApi.stats(30);
      setLoginStats(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    }
  };

  const handleExportLogsPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await loginLogsApi.exportPdf(loginLogsFilters);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `journal_connexions_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleDeleteLog = async (logId) => {
    try {
      await loginLogsApi.delete(logId);
      toast.success('Entrée supprimée');
      loadLoginLogs();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  useEffect(() => {
    if (activeTab === 'logs' && user?.role === 'admin') {
      loadLoginLogs();
    }
  }, [loginLogsPage, loginLogsFilters]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Load functions
  const loadUsers = async () => {
    try {
      setLoading(true);
      const [usersRes, pendingRes] = await Promise.all([
        api.get('/users'),
        api.get('/users/pending')
      ]);
      setUsers(usersRes.data);
      setPendingUsers(pendingRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/users/pending');
      setPendingUsers(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const loadSmtpConfig = async () => {
    try {
      const res = await api.get('/settings/smtp');
      if (res.data) {
        setSmtpConfig({ ...smtpConfig, ...res.data, password: '' });
        setSmtpStatus({ is_connected: res.data.is_connected, last_test: res.data.last_test });
      }
    } catch (error) {
      toast.error('Erreur lors du chargement de la configuration SMTP');
    }
  };

  const loadEmailTemplates = async () => {
    try {
      const res = await api.get('/settings/email-templates');
      setEmailTemplates(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des modèles email');
    }
  };

  const loadBillingSettings = async () => {
    try {
      const res = await api.get('/settings/billing');
      if (res.data) {
        setBillingSettings(res.data);
      }
      // Also load scheduler status
      loadSchedulerStatus();
    } catch (error) {
      toast.error('Erreur lors du chargement des paramètres de facturation');
    }
  };

  const loadSchedulerStatus = async () => {
    setLoadingScheduler(true);
    try {
      const [statusRes, pendingRes] = await Promise.all([
        api.get('/reminders/scheduler-status'),
        api.get('/reminders/pending-summary')
      ]);
      setSchedulerStatus(statusRes.data);
      setPendingReminders(pendingRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du planificateur');
    } finally {
      setLoadingScheduler(false);
    }
  };

  const handleProcessRemindersManually = async () => {
    setProcessingReminders(true);
    try {
      const res = await api.post('/reminders/process-automatic');
      toast.success(`${res.data.reminders_sent} relance(s) envoyée(s)`);
      loadSchedulerStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du traitement des relances');
    } finally {
      setProcessingReminders(false);
    }
  };

  const handleSendTestReminder = async () => {
    if (!testEmail.trim()) {
      toast.error('Veuillez saisir une adresse email');
      return;
    }
    setSendingTestEmail(true);
    try {
      const res = await api.post('/reminders/send-test', { email: testEmail.trim() });
      toast.success(res.data.message || 'Email de test envoyé avec succès');
      setTestEmailDialogOpen(false);
      setTestEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du test');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // User management handlers
  const handleCreateUser = () => {
    setSelectedUser(null);
    setUserFormData({ email: '', password: '', first_name: '', last_name: '', role: 'utilisateur', status: 'active' });
    setUserDialogOpen(true);
  };

  const handleEditUser = (u) => {
    setSelectedUser(u);
    setUserFormData({
      email: u.email,
      password: '',
      first_name: u.first_name,
      last_name: u.last_name,
      role: u.role,
      status: u.status || 'active'
    });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    try {
      if (selectedUser) {
        const updateData = { ...userFormData };
        if (!updateData.password) delete updateData.password;
        await api.put(`/users/${selectedUser.id}`, updateData);
        toast.success('Utilisateur mis à jour');
      } else {
        await api.post('/users', userFormData);
        toast.success('Utilisateur créé');
      }
      setUserDialogOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success('Utilisateur supprimé');
      setDeleteUserDialogOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await api.post(`/users/${userId}/approve`);
      toast.success('Compte approuvé');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await api.post(`/users/${userId}/reject`);
      toast.success('Demande rejetée');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // SMTP handlers
  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/settings/smtp', smtpConfig);
      setSmtpStatus({ is_connected: res.data.is_connected, last_test: res.data.last_test });
      toast.success('Configuration SMTP enregistrée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const res = await api.post('/settings/smtp/test');
      setSmtpStatus({ is_connected: true, last_test: new Date().toISOString() });
      toast.success(res.data.message);
    } catch (error) {
      setSmtpStatus({ ...smtpStatus, is_connected: false });
      toast.error(error.response?.data?.detail || 'Échec du test');
    } finally {
      setTestingSmtp(false);
    }
  };

  // Email template handlers
  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/settings/email-templates/${selectedTemplate.template_type}`, {
        template_type: selectedTemplate.template_type,
        subject: selectedTemplate.subject,
        body_html: selectedTemplate.body_html
      });
      toast.success('Modèle enregistré');
      setTemplateDialogOpen(false);
      loadEmailTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Billing settings handlers
  const handleSaveBilling = async (e) => {
    e.preventDefault();
    try {
      await api.post('/settings/billing', billingSettings);
      toast.success('Paramètres de facturation enregistrés');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const isAdmin = user?.role === 'admin';
  const isGestionnaire = user?.role === 'gestionnaire';

  return (
    <Layout>
      <PageHeader 
        title="Paramètres" 
        description="Gérez votre profil et les paramètres de l'application"
      />

      <div className="p-8" data-testid="settings-content">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="profile" data-testid="tab-profile">
              <User className="mr-2 h-4 w-4" />
              Profil
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">
              <ShieldCheck className="mr-2 h-4 w-4" />
              Sécurité
            </TabsTrigger>
            {(isAdmin || isGestionnaire) && (
              <TabsTrigger value="users" data-testid="tab-users">
                <Users className="mr-2 h-4 w-4" />
                Utilisateurs
              </TabsTrigger>
            )}
            {isAdmin && (
              <>
                <TabsTrigger value="logs" data-testid="tab-logs">
                  <History className="mr-2 h-4 w-4" />
                  Journal connexions
                </TabsTrigger>
                <TabsTrigger value="smtp" data-testid="tab-smtp">
                  <Mail className="mr-2 h-4 w-4" />
                  Configuration Email
                </TabsTrigger>
                <TabsTrigger value="emails" data-testid="tab-emails">
                  <FileText className="mr-2 h-4 w-4" />
                  Modèles d'emails
                </TabsTrigger>
                <TabsTrigger value="billing" data-testid="tab-billing">
                  <Building className="mr-2 h-4 w-4" />
                  Facturation
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="max-w-2xl">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
                    Informations personnelles
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="h-20 w-20 rounded-full bg-[#1e3a8a] flex items-center justify-center text-white text-2xl font-semibold">
                      {profileData.first_name?.[0]}{profileData.last_name?.[0]}
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-slate-900">
                        {profileData.first_name} {profileData.last_name}
                      </p>
                      <Badge className={roleConfig[user?.role]?.color}>
                        {roleConfig[user?.role]?.label}
                      </Badge>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prénom</Label>
                      <Input value={profileData.first_name} onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Nom</Label>
                      <Input value={profileData.last_name} onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={profileData.email} disabled className="bg-slate-50" />
                  </div>

                  <Button className="bg-[#1e3a8a] hover:bg-[#1e40af]" onClick={() => toast.success('Profil mis à jour')}>
                    <Save className="mr-2 h-4 w-4" />
                    Enregistrer
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Security Tab - 2FA */}
          <TabsContent value="security">
            <div className="max-w-2xl">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-[#1e3a8a] flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif' }}>
                    <ShieldCheck className="h-5 w-5" />
                    Authentification à deux facteurs (2FA)
                  </CardTitle>
                  <CardDescription>
                    Renforcez la sécurité de votre compte avec un code de vérification
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Current Status */}
                  <div className={`p-4 rounded-lg border ${twoFactorStatus.enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {twoFactorStatus.enabled ? (
                          <CheckCircle className="h-6 w-6 text-emerald-600" />
                        ) : (
                          <ShieldOff className="h-6 w-6 text-slate-400" />
                        )}
                        <div>
                          <p className={`font-medium ${twoFactorStatus.enabled ? 'text-emerald-800' : 'text-slate-600'}`}>
                            {twoFactorStatus.enabled ? 'Authentification 2FA activée' : 'Authentification 2FA désactivée'}
                          </p>
                          {twoFactorStatus.forced && (
                            <p className="text-sm text-amber-600">
                              <AlertCircle className="inline h-4 w-4 mr-1" />
                              Activée par un administrateur
                            </p>
                          )}
                        </div>
                      </div>
                      {!twoFactorStatus.enabled ? (
                        <Button 
                          onClick={handleSetup2FA} 
                          className="bg-[#1e3a8a] hover:bg-[#1e40af]"
                          disabled={setting2FA}
                          data-testid="setup-2fa-button"
                        >
                          {setting2FA ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <QrCode className="mr-2 h-4 w-4" />}
                          Configurer 2FA
                        </Button>
                      ) : !twoFactorStatus.forced && (
                        <Button 
                          variant="outline" 
                          onClick={() => setDisableDialogOpen(true)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          data-testid="disable-2fa-button"
                        >
                          <ShieldOff className="mr-2 h-4 w-4" />
                          Désactiver
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-slate-800">Comment ça marche ?</h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600">
                      <li>Installez une application d'authentification (Google Authenticator, Authy, Microsoft Authenticator)</li>
                      <li>Scannez le QR code ou entrez le code secret manuellement</li>
                      <li>À chaque connexion, entrez le code à 6 chiffres généré par l'application</li>
                    </ol>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Login Logs Tab - Admin only */}
          {isAdmin && (
            <TabsContent value="logs">
              <LoginLogsTab
                loginLogs={loginLogs} loginStats={loginStats} loginLogsTotal={loginLogsTotal}
                loginLogsPage={loginLogsPage} setLoginLogsPage={setLoginLogsPage}
                loginLogsFilters={loginLogsFilters} setLoginLogsFilters={setLoginLogsFilters}
                loadingLogs={loadingLogs} loadLoginLogs={loadLoginLogs} loadLoginStats={loadLoginStats}
                onExportPdf={handleExportLogsPdf} exportingPdf={exportingPdf}
                onDeleteLog={handleDeleteLog}
              />
            </TabsContent>
          )}

          {/* Users Tab */}
          {(isAdmin || isGestionnaire) && (
            <TabsContent value="users">
              <div className="space-y-6">
                {/* Pending Users */}
                {pendingUsers.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50">
                    <CardHeader>
                      <CardTitle className="text-amber-800 flex items-center gap-2">
                        <Clock className="h-5 w-5" />
                        Comptes en attente de validation ({pendingUsers.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nom</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Date d'inscription</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingUsers.map((u) => (
                            <TableRow key={u.id}>
                              <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                              <TableCell>{u.email}</TableCell>
                              <TableCell>{format(parseISO(u.created_at), 'd MMM yyyy', { locale: fr })}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button size="sm" onClick={() => handleApproveUser(u.id)} className="bg-emerald-600 hover:bg-emerald-700">
                                    <CheckCircle className="mr-1 h-4 w-4" />
                                    Approuver
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => handleRejectUser(u.id)} className="text-red-600 border-red-300">
                                    <XCircle className="mr-1 h-4 w-4" />
                                    Refuser
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* All Users (Admin only) */}
                {isAdmin && (
                  <Card className="border-slate-200">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
                        Tous les utilisateurs
                      </CardTitle>
                      <Button onClick={handleCreateUser} className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="add-user-button">
                        <Plus className="mr-2 h-4 w-4" />
                        Nouvel utilisateur
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {loading ? (
                        <div className="flex justify-center py-8">
                          <div className="spinner" style={{ width: 40, height: 40 }} />
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Utilisateur</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Rôle</TableHead>
                              <TableHead>Statut</TableHead>
                              <TableHead>2FA</TableHead>
                              <TableHead>Créé le</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {users.filter(u => u.status !== 'pending').map((u) => {
                              const StatusIcon = statusConfig[u.status]?.icon || CheckCircle;
                              return (
                                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                                  <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                                  <TableCell>{u.email}</TableCell>
                                  <TableCell>
                                    <Badge className={roleConfig[u.role]?.color}>{roleConfig[u.role]?.label}</Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Badge className={statusConfig[u.status]?.color}>
                                      <StatusIcon className="mr-1 h-3 w-3" />
                                      {statusConfig[u.status]?.label}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      {u.totp_enabled ? (
                                        <Badge className="bg-emerald-100 text-emerald-800">
                                          <ShieldCheck className="mr-1 h-3 w-3" />
                                          Actif
                                        </Badge>
                                      ) : (
                                        <Badge className="bg-slate-100 text-slate-600">
                                          <ShieldOff className="mr-1 h-3 w-3" />
                                          Inactif
                                        </Badge>
                                      )}
                                      {u.totp_forced && (
                                        <Badge className="bg-amber-100 text-amber-700 text-xs">Forcé</Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>{format(parseISO(u.created_at), 'd MMM yyyy', { locale: fr })}</TableCell>
                                  <TableCell className="text-right">
                                    {u.id !== user.id && (
                                      <div className="flex justify-end gap-1">
                                        <Button variant="ghost" size="sm" onClick={() => handleEditUser(u)} title="Modifier">
                                          <Pencil className="h-4 w-4" />
                                        </Button>
                                        {!u.totp_forced ? (
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="text-amber-600" 
                                            onClick={() => handleForce2FA(u.id, true)}
                                            title="Forcer 2FA"
                                          >
                                            <ShieldCheck className="h-4 w-4" />
                                          </Button>
                                        ) : (
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="text-slate-500" 
                                            onClick={() => handleForce2FA(u.id, false)}
                                            title="Désactiver 2FA forcée"
                                          >
                                            <ShieldOff className="h-4 w-4" />
                                          </Button>
                                        )}
                                        {u.totp_enabled && (
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="text-blue-600" 
                                            onClick={() => handleReset2FA(u.id)}
                                            title="Réinitialiser 2FA"
                                          >
                                            <RefreshCw className="h-4 w-4" />
                                          </Button>
                                        )}
                                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => { setSelectedUser(u); setDeleteUserDialogOpen(true); }} title="Supprimer">
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          )}

          {/* SMTP Configuration Tab (Admin only) */}
          {isAdmin && (
            <TabsContent value="smtp">
              <div className="max-w-2xl">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
                      Configuration SMTP
                    </CardTitle>
                    <CardDescription>
                      Configurez le serveur d'envoi d'emails (Gmail, Outlook, etc.)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Connection Status */}
                    <div className={`mb-6 p-4 rounded-lg border ${smtpStatus.is_connected ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                      <div className="flex items-center gap-3">
                        {smtpStatus.is_connected ? (
                          <CheckCircle className="h-5 w-5 text-emerald-600" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-slate-400" />
                        )}
                        <div>
                          <p className={`font-medium ${smtpStatus.is_connected ? 'text-emerald-800' : 'text-slate-600'}`}>
                            {smtpStatus.is_connected ? 'Connecté' : 'Non configuré'}
                          </p>
                          {smtpStatus.last_test && (
                            <p className="text-sm text-slate-500">
                              Dernier test : {format(parseISO(smtpStatus.last_test), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <form onSubmit={handleSaveSmtp} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={smtpConfig.email}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, email: e.target.value })}
                          placeholder="reservations@diocese-pamiers.fr"
                          required
                          data-testid="smtp-email-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Mot de passe *</Label>
                        <Input
                          type="password"
                          value={smtpConfig.password}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, password: e.target.value })}
                          placeholder="••••••••"
                          required={!smtpStatus.is_connected}
                          data-testid="smtp-password-input"
                        />
                        <p className="text-xs text-slate-500">Pour Gmail ou Microsoft 365, utilisez un mot de passe d'application</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Serveur SMTP *</Label>
                          <Input
                            value={smtpConfig.smtp_server}
                            onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_server: e.target.value })}
                            placeholder="smtp.office365.com"
                            required
                            data-testid="smtp-server-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Port *</Label>
                          <Input
                            type="number"
                            value={smtpConfig.smtp_port}
                            onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_port: parseInt(e.target.value) })}
                            required
                            data-testid="smtp-port-input"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={smtpConfig.use_tls}
                            onCheckedChange={(checked) => setSmtpConfig({ ...smtpConfig, use_tls: checked, use_ssl: checked ? false : smtpConfig.use_ssl })}
                            data-testid="smtp-tls-switch"
                          />
                          <Label>TLS (STARTTLS)</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={smtpConfig.use_ssl}
                            onCheckedChange={(checked) => setSmtpConfig({ ...smtpConfig, use_ssl: checked, use_tls: checked ? false : smtpConfig.use_tls })}
                            data-testid="smtp-ssl-switch"
                          />
                          <Label>SSL</Label>
                        </div>
                      </div>

                      <div className="flex gap-3 pt-4">
                        <Button type="submit" className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="save-smtp-button">
                          <Save className="mr-2 h-4 w-4" />
                          Enregistrer
                        </Button>
                        <Button type="button" variant="outline" onClick={handleTestSmtp} disabled={testingSmtp} data-testid="test-smtp-button">
                          {testingSmtp ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                          Tester la connexion
                        </Button>
                      </div>
                    </form>

                    {/* SMTP Presets */}
                    <div className="mt-6 pt-6 border-t">
                      <p className="text-sm font-medium text-slate-700 mb-3">Configurations courantes :</p>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => setSmtpConfig({ ...smtpConfig, smtp_server: 'smtp.gmail.com', smtp_port: 587, use_tls: true, use_ssl: false })}>
                          Gmail
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setSmtpConfig({ ...smtpConfig, smtp_server: 'smtp.office365.com', smtp_port: 587, use_tls: true, use_ssl: false })}>
                          Outlook/Office 365
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setSmtpConfig({ ...smtpConfig, smtp_server: 'smtp.orange.fr', smtp_port: 465, use_tls: false, use_ssl: true })}>
                          Orange
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setSmtpConfig({ ...smtpConfig, smtp_server: 'smtp.free.fr', smtp_port: 587, use_tls: true, use_ssl: false })}>
                          Free
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Email Templates Tab (Admin only) */}
          {isAdmin && (
            <TabsContent value="emails">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
                    Modèles d'emails
                  </CardTitle>
                  <CardDescription>
                    Personnalisez le contenu des emails automatiques
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type d'email</TableHead>
                        <TableHead>Sujet</TableHead>
                        <TableHead>Dernière modification</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {emailTemplates.map((template) => (
                        <TableRow key={template.template_type}>
                          <TableCell className="font-medium">
                            {emailTemplateLabels[template.template_type] || template.template_type}
                          </TableCell>
                          <TableCell className="text-slate-600">{template.subject || '(Par défaut)'}</TableCell>
                          <TableCell className="text-slate-500">
                            {template.updated_at ? format(parseISO(template.updated_at), 'd MMM yyyy', { locale: fr }) : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => handleEditTemplate(template)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Billing Settings Tab (Admin only) */}
          {isAdmin && (
            <TabsContent value="billing">
              <BillingSettingsTab
                billingSettings={billingSettings} setBillingSettings={setBillingSettings} onSave={handleSaveBilling}
                schedulerStatus={schedulerStatus} loadSchedulerStatus={loadSchedulerStatus} loadingScheduler={loadingScheduler}
                pendingReminders={pendingReminders} onProcessReminders={handleProcessRemindersManually} processingReminders={processingReminders}
                onOpenTestEmail={() => setTestEmailDialogOpen(true)}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a8a]">
              {selectedUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom *</Label>
                <Input
                  value={userFormData.first_name}
                  onChange={(e) => setUserFormData({ ...userFormData, first_name: e.target.value })}
                  required
                  data-testid="user-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={userFormData.last_name}
                  onChange={(e) => setUserFormData({ ...userFormData, last_name: e.target.value })}
                  required
                  data-testid="user-lastname-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                required
                disabled={!!selectedUser}
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{selectedUser ? 'Nouveau mot de passe' : 'Mot de passe *'}</Label>
              <Input
                type="password"
                value={userFormData.password}
                onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                required={!selectedUser}
                placeholder={selectedUser ? 'Laisser vide pour ne pas modifier' : ''}
                data-testid="user-password-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select value={userFormData.role} onValueChange={(v) => setUserFormData({ ...userFormData, role: v })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrateur</SelectItem>
                    <SelectItem value="gestionnaire">Gestionnaire</SelectItem>
                    <SelectItem value="utilisateur">Utilisateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={userFormData.status} onValueChange={(v) => setUserFormData({ ...userFormData, status: v })}>
                  <SelectTrigger data-testid="user-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="suspended">Suspendu</SelectItem>
                    <SelectItem value="blocked">Bloqué</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="save-user-button">
                {selectedUser ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <AlertDialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. L'utilisateur "{selectedUser?.first_name} {selectedUser?.last_name}" sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a8a]">
              Modifier le modèle : {emailTemplateLabels[selectedTemplate?.template_type]}
            </DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <form onSubmit={handleSaveTemplate} className="space-y-4">
              <div className="space-y-2">
                <Label>Sujet de l'email</Label>
                <Input
                  value={selectedTemplate.subject}
                  onChange={(e) => setSelectedTemplate({ ...selectedTemplate, subject: e.target.value })}
                  data-testid="template-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Contenu (HTML)</Label>
                <Textarea
                  value={selectedTemplate.body_html}
                  onChange={(e) => setSelectedTemplate({ ...selectedTemplate, body_html: e.target.value })}
                  rows={15}
                  className="font-mono text-sm"
                  data-testid="template-body-input"
                />
                <p className="text-xs text-slate-500">
                  Variables disponibles : {"{{first_name}}"}, {"{{last_name}}"}, {"{{email}}"}, {"{{client_name}}"}, {"{{quote_number}}"}, {"{{invoice_number}}"}, {"{{amount}}"}, {"{{due_date}}"}, {"{{login_url}}"}
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)}>Annuler</Button>
                <Button type="submit" className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="save-template-button">
                  Enregistrer
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* 2FA Setup Dialog */}
      <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a8a] flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Configuration 2FA
            </DialogTitle>
          </DialogHeader>
          {setupData && (
            <form onSubmit={handleVerifySetup} className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-4">
                  Scannez ce QR code avec votre application d'authentification
                </p>
                <div className="flex justify-center mb-4">
                  <img 
                    src={`data:image/png;base64,${setupData.qr_code}`} 
                    alt="QR Code 2FA"
                    className="border rounded-lg"
                    data-testid="2fa-qr-code"
                  />
                </div>
                <div className="bg-slate-100 rounded p-3 mb-4">
                  <p className="text-xs text-slate-500 mb-1">Ou entrez ce code manuellement :</p>
                  <code className="text-sm font-mono font-bold text-slate-800 select-all" data-testid="2fa-secret">
                    {setupData.secret}
                  </code>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Code de vérification</Label>
                <Input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                  autoFocus
                  data-testid="2fa-setup-code-input"
                />
                <p className="text-xs text-slate-500">Entrez le code à 6 chiffres affiché dans votre application</p>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setSetupDialogOpen(false); setSetupData(null); setVerificationCode(''); }}>
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#1e3a8a] hover:bg-[#1e40af]"
                  disabled={verificationCode.length !== 6 || setting2FA}
                  data-testid="2fa-setup-verify-button"
                >
                  {setting2FA ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                  Activer 2FA
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* 2FA Disable Dialog */}
      <Dialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <ShieldOff className="h-5 w-5" />
              Désactiver l'authentification 2FA
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDisable2FA} className="space-y-4">
            <p className="text-sm text-slate-600">
              Pour désactiver la 2FA, veuillez entrer le code actuel de votre application d'authentification.
            </p>
            
            <div className="space-y-2">
              <Label>Code de vérification</Label>
              <Input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center text-2xl tracking-widest font-mono"
                maxLength={6}
                autoFocus
                data-testid="2fa-disable-code-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDisableDialogOpen(false); setVerificationCode(''); }}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="bg-red-600 hover:bg-red-700"
                disabled={verificationCode.length !== 6 || setting2FA}
                data-testid="2fa-disable-confirm-button"
              >
                {setting2FA ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldOff className="mr-2 h-4 w-4" />}
                Désactiver 2FA
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Test Reminder Email Dialog */}
      <Dialog open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
              <Mail className="inline-block mr-2 h-5 w-5" />
              Tester l'envoi de relance
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Envoyez un email de test de relance à l'adresse de votre choix pour vérifier le bon fonctionnement du système.
            </p>
            <div className="space-y-2">
              <Label htmlFor="test-email">Adresse email de test *</Label>
              <Input
                id="test-email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="votre@email.com"
                data-testid="test-reminder-email-input"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <p>L'email de test utilisera le template de relance J+7 avec des données fictives.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTestEmailDialogOpen(false); setTestEmail(''); }}>
              Annuler
            </Button>
            <Button 
              onClick={handleSendTestReminder}
              className="bg-[#1e3a8a] hover:bg-[#1e40af]"
              disabled={sendingTestEmail || !testEmail.trim()}
              data-testid="send-test-reminder-button"
            >
              {sendingTestEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Envoyer le test
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
